import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { 
  Wine, 
  ChevronRight, 
  Trophy, 
  Clock, 
  Zap, 
  AlertCircle, 
  Beaker,
  BookOpen,
  CheckCircle2,
  XCircle,
  RotateCcw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PracticeTestPage() {
  const { practiceCode } = useParams();
  const [practiceData, setPracticeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Test state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [results, setResults] = useState(null);
  
  // Practice-specific state
  const [ingredientAnswers, setIngredientAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);

  useEffect(() => {
    fetchPracticeTest();
  }, [practiceCode]);

  const fetchPracticeTest = async () => {
    try {
      const response = await axios.get(`${API}/practice/${practiceCode}`);
      setPracticeData(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError("Practice link not found or invalid");
      } else {
        setError("Failed to load practice test");
      }
    } finally {
      setLoading(false);
    }
  };

  const test = practiceData?.test;
  const cocktails = practiceData?.cocktails || [];
  const ingredientsQuestions = practiceData?.ingredients_questions || [];
  const currentCocktail = cocktails[currentQuestion];
  const testType = test?.test_type || "full";
  const isIngredientsOnly = testType === "ingredients";
  
  const currentIngredientQuestion = isIngredientsOnly ? ingredientsQuestions[currentQuestion] : null;
  const totalQuestions = isIngredientsOnly ? ingredientsQuestions.length : cocktails.length;

  const generateOptions = (cocktail, field, allCocktails) => {
    const correctValue = cocktail[field] || "None";
    let options = new Set([correctValue]);
    
    allCocktails.forEach(c => {
      if (c.id !== cocktail.id && c[field]) {
        options.add(c[field]);
      }
    });
    
    const genericOptions = {
      garnish: ["Lime wheel", "Orange peel", "Cherry", "Mint sprig", "None", "Olive", "Lemon twist"],
      glassware: ["Rocks", "Coupe", "Highball", "Martini", "Collins", "Nick & Nora", "Flute"],
      side_elements: ["None", "N/A", "Espresso shot", "Bitters on the side", "Sugar rim", "Salt rim"]
    };
    
    if (genericOptions[field]) {
      genericOptions[field].forEach(opt => options.add(opt));
    }
    
    return Array.from(options).slice(0, 4).sort(() => Math.random() - 0.5);
  };

  const generateIngredientOptions = (cocktail, allCocktails) => {
    const correctIngredients = cocktail.ingredients?.map(i => 
      `${i.measurement} ${i.name}${i.brand ? ` (${i.brand})` : ''}`
    ).join(", ") || "None";
    
    let options = new Set([correctIngredients]);
    
    allCocktails.forEach(c => {
      if (c.id !== cocktail.id && c.ingredients?.length > 0) {
        const ing = c.ingredients.map(i => 
          `${i.measurement} ${i.name}${i.brand ? ` (${i.brand})` : ''}`
        ).join(", ");
        options.add(ing);
      }
    });
    
    return Array.from(options).slice(0, 4).sort(() => Math.random() - 0.5);
  };

  const getQuestionsForTestType = () => {
    switch (testType) {
      case "ingredients":
        return ["ingredients"];
      case "presentation":
        return ["garnish", "glassware"];
      case "speed":
      case "full":
      default:
        return ["ingredients", "garnish", "glassware", "side_elements"];
    }
  };

  const questions = getQuestionsForTestType();

  const startTest = () => {
    setTestStarted(true);
    setCorrectCount(0);
    setIncorrectCount(0);
  };

  const handleAnswer = (field, value) => {
    setAnswers(prev => ({
      ...prev,
      [currentCocktail.id]: {
        ...prev[currentCocktail.id],
        [field]: value
      }
    }));
  };

  const handleIngredientAnswer = (questionIndex, value) => {
    setIngredientAnswers(prev => ({
      ...prev,
      [questionIndex]: value
    }));
    setCurrentAnswer(value);
  };

  // Submit answer and show immediate feedback
  const submitAnswer = () => {
    if (isIngredientsOnly) {
      const isCorrect = currentAnswer === currentIngredientQuestion.correct_answer;
      if (isCorrect) {
        setCorrectCount(prev => prev + 1);
      } else {
        setIncorrectCount(prev => prev + 1);
      }
    }
    setShowFeedback(true);
  };

  const nextQuestion = () => {
    setShowFeedback(false);
    setCurrentAnswer(null);
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      finishPractice();
    }
  };

  const finishPractice = () => {
    const percentage = totalQuestions > 0 ? (correctCount / totalQuestions) * 100 : 0;
    setResults({
      correct: correctCount + (showFeedback && currentAnswer === currentIngredientQuestion?.correct_answer ? 1 : 0),
      total: totalQuestions,
      percentage: totalQuestions > 0 
        ? ((correctCount + (showFeedback && currentAnswer === currentIngredientQuestion?.correct_answer ? 1 : 0)) / totalQuestions) * 100 
        : 0
    });
    setTestCompleted(true);
  };

  const restartPractice = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setIngredientAnswers({});
    setTestStarted(false);
    setTestCompleted(false);
    setResults(null);
    setShowFeedback(false);
    setCurrentAnswer(null);
    setCorrectCount(0);
    setIncorrectCount(0);
    // Re-fetch to get shuffled questions
    fetchPracticeTest();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-[hsl(var(--muted-foreground))]">Loading practice test...</div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Unable to Load Practice</h2>
            <p className="text-[hsl(var(--muted-foreground))]">{error}</p>
          </CardContent>
        </Card>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // Results Screen
  if (testCompleted && results) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-24">
        <div className="max-w-2xl mx-auto space-y-6">
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-8 text-center">
              <Trophy size={64} className={`mx-auto mb-4 ${
                results.percentage >= 80 ? 'text-green-500' : results.percentage >= 60 ? 'text-[#C36A2F]' : 'text-red-500'
              }`} />
              <h1 className="text-3xl font-bold mb-2" data-testid="practice-complete-title">Practice Complete!</h1>
              <p className="text-[hsl(var(--muted-foreground))] mb-2">{test?.name}</p>
              <span className="inline-block px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 mb-6">
                <BookOpen size={12} className="inline mr-1" /> Practice Mode
              </span>
              
              <div className={`inline-block px-8 py-4 rounded-xl border-2 ${
                results.percentage >= 80 
                  ? 'border-green-500 bg-green-500/10' 
                  : results.percentage >= 60 
                  ? 'border-[#C36A2F] bg-[#C36A2F]/10'
                  : 'border-red-500 bg-red-500/10'
              }`}>
                <p className={`text-5xl font-bold ${
                  results.percentage >= 80 ? 'text-green-500' : results.percentage >= 60 ? 'text-[#C36A2F]' : 'text-red-500'
                }`} data-testid="practice-score">
                  {results.percentage.toFixed(0)}%
                </p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {results.correct} / {results.total} correct
                </p>
              </div>
              
              <div className="mt-8">
                <Button
                  onClick={restartPractice}
                  className="btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
                  data-testid="restart-practice-btn"
                >
                  <RotateCcw size={18} className="mr-2" />
                  Practice Again
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-[hsl(var(--muted-foreground))]">
            Practice mode - your results are not recorded.
          </p>
        </div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // Start Screen
  if (!testStarted) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <BookOpen size={48} className="mx-auto text-blue-400 mb-4" />
              <span className="inline-block px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 mb-4">
                Practice Mode
              </span>
              <h1 className="text-2xl font-bold mb-2">{test?.name}</h1>
              {test?.description && (
                <p className="text-[hsl(var(--muted-foreground))]">{test.description}</p>
              )}
              
              <div className="mt-4 p-4 rounded-lg bg-[hsl(var(--secondary))]">
                <p className="font-medium text-lg text-blue-400">
                  Learn at your own pace!
                </p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                  Immediate feedback after each question
                </p>
              </div>
              
              <div className="mt-4 space-y-2 text-sm text-[hsl(var(--muted-foreground))]">
                {isIngredientsOnly ? (
                  <>
                    <p>{ingredientsQuestions.length} ingredient questions</p>
                    <p className="text-[hsl(var(--primary))]">
                      Format: &quot;How much of [ingredient] is in [cocktail]?&quot;
                    </p>
                  </>
                ) : (
                  <>
                    <p>{cocktails.length} cocktails</p>
                    <p>{questions.length} question{questions.length > 1 ? 's' : ''} per cocktail</p>
                  </>
                )}
              </div>
            </div>

            <Button
              onClick={startTest}
              data-testid="start-practice-btn"
              className="w-full btn-glow bg-blue-500 text-white hover:bg-blue-600"
            >
              Start Practice
              <ChevronRight size={18} className="ml-2" />
            </Button>
          </CardContent>
        </Card>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // Quiz Screen with Immediate Feedback
  const currentAnswerSelected = isIngredientsOnly 
    ? ingredientAnswers[currentQuestion] !== undefined 
    : false;

  // For ingredients-only test
  if (isIngredientsOnly && currentIngredientQuestion) {
    const isCorrect = currentAnswer === currentIngredientQuestion.correct_answer;
    
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-24">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Header with progress */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                Practice
              </span>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Question {currentQuestion + 1} of {totalQuestions}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-green-400 text-sm">{correctCount} ✓</span>
              <span className="text-red-400 text-sm">{incorrectCount} ✗</span>
            </div>
          </div>

          <Progress value={(currentQuestion / totalQuestions) * 100} className="h-2" />

          {/* Question Card */}
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <span className="inline-block px-3 py-1 rounded-full text-xs bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] mb-4">
                  {currentIngredientQuestion.cocktail_name}
                </span>
                <h2 className="text-xl md:text-2xl font-bold text-[hsl(var(--foreground))]" data-testid="practice-question">
                  How much of <span className="text-[hsl(var(--primary))]">{currentIngredientQuestion.ingredient_name}</span> is in the <span className="text-[hsl(var(--primary))]">{currentIngredientQuestion.cocktail_name}</span>?
                </h2>
              </div>
              
              <RadioGroup 
                value={ingredientAnswers[currentQuestion] || ""} 
                onValueChange={(val) => handleIngredientAnswer(currentQuestion, val)}
                className="space-y-3"
                disabled={showFeedback}
              >
                {currentIngredientQuestion.options.map((opt, idx) => {
                  let optionStyle = 'border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]';
                  
                  if (showFeedback) {
                    if (opt === currentIngredientQuestion.correct_answer) {
                      optionStyle = 'border-green-500 bg-green-500/20';
                    } else if (opt === currentAnswer && opt !== currentIngredientQuestion.correct_answer) {
                      optionStyle = 'border-red-500 bg-red-500/20';
                    }
                  } else if (ingredientAnswers[currentQuestion] === opt) {
                    optionStyle = 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10';
                  }
                  
                  return (
                    <div 
                      key={`practice-ing-${opt}`} 
                      className={`flex items-center space-x-3 p-4 rounded-lg cursor-pointer transition-all border ${optionStyle}`}
                      data-testid={`practice-option-${idx}`}
                    >
                      <RadioGroupItem value={opt} id={`ing-opt-${idx}`} disabled={showFeedback} />
                      <Label htmlFor={`ing-opt-${idx}`} className="text-base cursor-pointer flex-1 font-medium">
                        {opt}
                      </Label>
                      {showFeedback && opt === currentIngredientQuestion.correct_answer && (
                        <CheckCircle2 className="text-green-500" size={20} />
                      )}
                      {showFeedback && opt === currentAnswer && opt !== currentIngredientQuestion.correct_answer && (
                        <XCircle className="text-red-500" size={20} />
                      )}
                    </div>
                  );
                })}
              </RadioGroup>

              {/* Feedback Section */}
              {showFeedback && (
                <div className={`mt-6 p-4 rounded-lg ${isCorrect ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {isCorrect ? (
                      <>
                        <CheckCircle2 className="text-green-500" size={24} />
                        <span className="text-green-400 font-bold text-lg">Correct!</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="text-red-500" size={24} />
                        <span className="text-red-400 font-bold text-lg">Incorrect</span>
                      </>
                    )}
                  </div>
                  {!isCorrect && (
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                      The correct answer is: <span className="text-green-400 font-medium">{currentIngredientQuestion.correct_answer}</span>
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex gap-3">
            <div className="flex-1" />
            {!showFeedback ? (
              <Button
                onClick={submitAnswer}
                disabled={!currentAnswerSelected}
                data-testid="submit-answer-btn"
                className="bg-blue-500 text-white hover:bg-blue-600"
              >
                Check Answer
              </Button>
            ) : (
              <Button
                onClick={nextQuestion}
                data-testid="next-question-btn"
                className="btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
              >
                {currentQuestion === totalQuestions - 1 ? "Finish Practice" : "Next Question"}
                <ChevronRight size={18} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // Regular quiz screen for other test types (simplified for practice)
  const currentAnswers = answers[currentCocktail?.id] || {};
  const ingredientOptions = currentCocktail ? generateIngredientOptions(currentCocktail, cocktails) : [];
  const garnishOptions = currentCocktail ? generateOptions(currentCocktail, 'garnish', cocktails) : [];
  const glasswareOptions = currentCocktail ? generateOptions(currentCocktail, 'glassware', cocktails) : [];
  const sideOptions = currentCocktail ? generateOptions(currentCocktail, 'side_elements', cocktails) : [];

  const answeredQuestions = questions.filter(q => currentAnswers[q]).length;
  const isQuestionComplete = answeredQuestions === questions.length;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-24">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
              Practice
            </span>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Cocktail {currentQuestion + 1} of {totalQuestions}
            </p>
          </div>
        </div>

        <Progress value={(currentQuestion / totalQuestions) * 100} className="h-2" />

        {/* Cocktail Name */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="p-6 text-center">
            <h2 className="text-3xl font-bold text-[hsl(var(--primary))]">{currentCocktail?.name}</h2>
            <p className="text-[hsl(var(--muted-foreground))]">{currentCocktail?.category} • {currentCocktail?.base_spirit}</p>
          </CardContent>
        </Card>

        {/* Questions - simplified view */}
        <div className="space-y-6">
          {questions.includes("ingredients") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">What are the ingredients?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.ingredients || ""} 
                  onValueChange={(val) => handleAnswer('ingredients', val)}
                >
                  {ingredientOptions.map((opt, idx) => (
                    <div 
                      key={`practice-ing2-${opt}`} 
                      className={`flex items-start space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.ingredients === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`ing-${idx}`} />
                      <Label htmlFor={`ing-${idx}`} className="text-sm cursor-pointer flex-1">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {questions.includes("garnish") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">What is the garnish?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.garnish || ""} 
                  onValueChange={(val) => handleAnswer('garnish', val)}
                >
                  {garnishOptions.map((opt, idx) => (
                    <div 
                      key={`practice-gar-${opt}`} 
                      className={`flex items-center space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.garnish === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`gar-${idx}`} />
                      <Label htmlFor={`gar-${idx}`} className="cursor-pointer">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {questions.includes("glassware") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">What glassware is used?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.glassware || ""} 
                  onValueChange={(val) => handleAnswer('glassware', val)}
                >
                  {glasswareOptions.map((opt, idx) => (
                    <div 
                      key={`practice-glass-${opt}`} 
                      className={`flex items-center space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.glassware === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`glass-${idx}`} />
                      <Label htmlFor={`glass-${idx}`} className="cursor-pointer">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {questions.includes("side_elements") && (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Any side elements?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={currentAnswers.side_elements || ""} 
                  onValueChange={(val) => handleAnswer('side_elements', val)}
                >
                  {sideOptions.map((opt, idx) => (
                    <div 
                      key={`practice-side-${opt || 'none'}`} 
                      className={`flex items-center space-x-3 p-3 rounded-lg quiz-option ${currentAnswers.side_elements === opt ? 'selected' : ''}`}
                    >
                      <RadioGroupItem value={opt} id={`side-${idx}`} />
                      <Label htmlFor={`side-${idx}`} className="cursor-pointer">{opt || "None"}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          <div className="flex-1" />
          <Button
            onClick={nextQuestion}
            disabled={!isQuestionComplete}
            className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
          >
            {currentQuestion === totalQuestions - 1 ? "Finish Practice" : "Next"}
            <ChevronRight size={18} className="ml-1" />
          </Button>
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
